/**
 * Lightweight i18n for the worker PWA. We keep it bespoke (no i18next) because
 * the surface is small and our bundles need to stay tiny on 4G.
 *
 * Locale is persisted in localStorage and applied to <html dir/lang> so the
 * RTL flip happens automatically with Tailwind's logical properties / start-end
 * utilities.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Locale = 'en' | 'ar';

type Dict = Record<string, { en: string; ar: string }>;

// Keep keys flat + descriptive. Add new strings here as the app grows.
const DICT: Dict = {
  'app.signed_in_as': { en: 'Signed in', ar: 'تم تسجيل الدخول' },
  'app.sign_out': { en: 'Sign out', ar: 'تسجيل الخروج' },
  'today.title': { en: 'Today', ar: 'اليوم' },
  'today.tab.day': { en: 'Today', ar: 'اليوم' },
  'today.tab.week': { en: 'Week', ar: 'الأسبوع' },
  'today.tab.calendar': { en: 'Calendar', ar: 'التقويم' },
  'today.week.empty_day': { en: 'No jobs', ar: 'لا توجد مهام' },
  'today.loading': { en: 'Loading your jobs…', ar: 'جارٍ تحميل المهام…' },
  'today.failed': { en: 'Failed to load jobs:', ar: 'فشل تحميل المهام:' },
  'today.empty': { en: 'No jobs scheduled for today. Enjoy the break ☕', ar: 'لا توجد مهام اليوم. استمتع بالاستراحة ☕' },
  'today.upcoming': { en: 'Upcoming', ar: 'القادمة' },
  'today.active': { en: 'Active', ar: 'نشط' },
  'today.done': { en: 'Done today', ar: 'منجز اليوم' },
  'today.stat_active': { en: 'Active', ar: 'نشط' },
  'today.stat_done': { en: 'Done', ar: 'منجز' },
  'today.stat_upcoming': { en: 'Upcoming', ar: 'قادم' },
  'today.all_done': { en: 'All today\u2019s jobs are complete. Great work!', ar: 'تم إنجاز جميع مهام اليوم. عمل رائع!' },
  'job.back': { en: 'Back', ar: 'رجوع' },
  'job.label': { en: 'Job', ar: 'مهمة' },
  'job.when': { en: 'When', ar: 'الموعد' },
  'job.estimated': { en: 'Estimated', ar: 'المدة المقدرة' },
  'job.minutes': { en: 'min', ar: 'دقيقة' },
  'job.priority': { en: 'priority', ar: 'الأولوية' },
  'job.status': { en: 'status', ar: 'الحالة' },
  'job.customer': { en: 'Customer', ar: 'العميل' },
  'job.team': { en: 'Team', ar: 'الفريق' },
  'job.team.you_solo': { en: "You're on this job", ar: 'أنت في هذه المهمة' },
  'job.team.with_others': { en: 'You + {n} other(s) on this job', ar: 'أنت + {n} آخرون في هذه المهمة' },
  'job.team.primary_badge': { en: 'Primary', ar: 'أساسي' },
  'job.notes': { en: 'Notes', ar: 'ملاحظات' },
  'job.anytime': { en: 'Anytime', ar: 'في أي وقت' },
  'today.you': { en: 'You', ar: 'أنت' },
  'job.not_assigned': { en: 'You are not assigned to this job — view only.', ar: 'أنت غير معيّن لهذه المهمة — للعرض فقط.' },
  'job.worker_notes': { en: 'My notes', ar: 'ملاحظاتي' },
  'job.worker_notes_placeholder': { en: 'Add notes about this job…', ar: 'أضف ملاحظات حول هذه المهمة…' },
  'job.worker_notes_save': { en: 'Save notes', ar: 'حفظ الملاحظات' },
  'job.worker_notes_saved': { en: 'Saved', ar: 'تم الحفظ' },
  'job.loading_customer': { en: 'Loading customer…', ar: 'جارٍ تحميل بيانات العميل…' },
  'job.loading': { en: 'Loading…', ar: 'جارٍ التحميل…' },
  'job.failed': { en: 'Failed:', ar: 'فشل:' },
  'job.makani': { en: 'Makani:', ar: 'مكاني:' },
  'job.action.on_my_way': { en: '📍 On my way', ar: '📍 في الطريق' },
  'job.action.start': { en: '▶ Start job', ar: '▶ بدء المهمة' },
  'job.action.hold': { en: '⏸ Hold', ar: '⏸ إيقاف مؤقت' },
  'job.action.complete': { en: '✓ Complete', ar: '✓ إكمال' },
  'job.action.completed': { en: '✓ Completed.', ar: '✓ مكتمل.' },
  'job.action.cancelled': { en: 'Cancelled.', ar: 'ملغي.' },
  'job.action.held_admin': { en: 'On hold — admin will reschedule', ar: 'معلق — سيقوم المسؤول بإعادة الجدولة' },
  'job.action.future_lock': { en: 'Starts on', ar: 'يبدأ في' },
  'job.overdue.title': { en: 'Job overdue —', ar: 'المهمة متأخرة —' },
  'job.overdue.minutes_short': { en: 'min', ar: 'د' },
  'job.overdue.prompt': {
    en: "Tap your ETA so the admin knows you're aware.",
    ar: 'اضغط على وقت وصولك حتى يعلم المسؤول أنك على دراية.',
  },
  'job.overdue.acked': { en: '✓ Admin notified', ar: '✓ تم إبلاغ المسؤول' },
  'job.overdue.acked_at': { en: 'Acknowledged at', ar: 'تم التأكيد في' },
  'job.complete_confirm': { en: 'Mark this job complete?', ar: 'تأكيد إنهاء المهمة؟' },
  'job.complete_title': { en: 'End job', ar: 'إنهاء المهمة' },
  'job.complete_subtitle': { en: 'This cannot be undone.', ar: 'لا يمكن التراجع عن هذا الإجراء.' },
  'job.hold.placeholder': { en: 'Reason for hold (required)', ar: 'سبب الإيقاف (مطلوب)' },
  'job.hold.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'job.hold.confirm': { en: 'Confirm hold', ar: 'تأكيد الإيقاف' },
  'proof.title': { en: 'Proof of work', ar: 'إثبات العمل' },
  'proof.photos': { en: 'photo', ar: 'صورة' },
  'proof.photos_plural': { en: 'photos', ar: 'صور' },
  'proof.uploading': { en: 'Uploading…', ar: 'جارٍ الرفع…' },
  'proof.add': { en: 'Add', ar: 'إضافة' },
  'proof.add_video': { en: 'Add video', ar: 'إضافة فيديو' },
  'proof.video_too_big': { en: 'Video must be under 75 MB / 60 sec.', ar: 'يجب أن يكون الفيديو أقل من 75 ميغا / 60 ثانية' },
  'proof.before': { en: 'before', ar: 'قبل' },
  'proof.after': { en: 'after', ar: 'بعد' },
  'proof.parts': { en: 'parts', ar: 'قطع' },
  'sig.title': { en: 'Customer signature', ar: 'توقيع العميل' },
  'sig.hint': {
    en: 'Hand the phone to the customer to sign. This confirms the job is complete.',
    ar: 'أعط الجهاز للعميل ليوقّع. هذا يؤكد إنجاز المهمة.',
  },
  'sig.clear': { en: 'Clear', ar: 'مسح' },
  'sig.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'sig.save': { en: 'Save & complete', ar: 'حفظ وإنجاز' },
  'sig.saving': { en: 'Saving…', ar: 'جارٍ الحفظ…' },
  'wa.title': { en: 'WhatsApp the customer', ar: 'مراسلة العميل عبر واتساب' },
  'wa.hint': {
    en: 'Opens WhatsApp with the message ready — just tap Send.',
    ar: 'يفتح واتساب برسالة جاهزة — فقط اضغط إرسال.',
  },
  'wa.on_the_way': { en: '📍 Tell admin: On my way', ar: '📍 أبلغ الإدارة: في الطريق' },
  'wa.eta_prompt': {
    en: 'How many minutes until you arrive?',
    ar: 'كم دقيقة تبقت حتى وصولك؟',
  },
  'wa.eta_title': { en: 'Notify admin you’re on the way', ar: 'أبلغ الإدارة أنك في الطريق' },
  'wa.eta_subtitle': {
    en: 'Pick how long until you arrive. WhatsApp will open with a message ready to send to admin.',
    ar: 'اختر الوقت المتبقي حتى وصولك. سيفتح واتساب برسالة جاهزة للإدارة.',
  },
  'wa.eta_custom': { en: 'Custom (minutes)', ar: 'مخصص (دقائق)' },
  'wa.eta_arrival': { en: 'Arrival around', ar: 'الوصول حوالي' },
  'wa.eta_send': { en: 'Send via WhatsApp', ar: 'إرسال عبر واتساب' },
  'wa.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'job.location': { en: 'Location', ar: 'الموقع' },
  'job.navigate': { en: 'Navigate', ar: 'الملاحة' },
  'job.open_maps': { en: 'Open in Google Maps', ar: 'فتح في خرائط Google' },
  'job.copy_address': { en: 'Copy address', ar: 'نسخ العنوان' },
  'job.address_copied': { en: 'Address copied', ar: 'تم نسخ العنوان' },
  'wa.started': { en: '▶ Tell customer: Job started', ar: '▶ أبلغ العميل: بدأت المهمة' },
  'wa.completed': { en: '✓ Tell customer: Completed + feedback', ar: '✓ أبلغ العميل: تم الإنجاز + تقييم' },
  'workflow.title': { en: 'Workflow', ar: 'سير العمل' },
  'workflow.step1': { en: 'On my way', ar: 'في الطريق' },
  'workflow.step2': { en: 'Start job', ar: 'بدء المهمة' },
  'workflow.step3': { en: 'Proof of work', ar: 'إثبات العمل' },
  'workflow.step4': { en: 'End job', ar: 'إنهاء المهمة' },
  'workflow.locked': { en: 'Complete the previous step first', ar: 'أكمل الخطوة السابقة أولاً' },
  'workflow.proof_required': {
    en: 'Upload before, after, and parts photos to enable End job.',
    ar: 'حمّل صور قبل وبعد وقطع الغيار لتمكين إنهاء المهمة.',
  },
  'workflow.proof_optional': {
    en: 'Photos are recommended but optional — you can end the job at any time.',
    ar: 'الصور موصى بها ولكنها اختيارية — يمكنك إنهاء المهمة في أي وقت.',
  },
  'workflow.cta.on_my_way': { en: '📍 Tell admin: On my way', ar: '📍 أبلغ الإدارة: في الطريق' },
  'workflow.cta.start': { en: '▶ Start job', ar: '▶ بدء المهمة' },
  'workflow.cta.end': { en: '✓ End job', ar: '✓ إنهاء المهمة' },
  'workflow.cta.hold': { en: '⏸ Hold job', ar: '⏸ إيقاف المهمة' },
  'workflow.held.title': { en: 'On hold — awaiting admin', ar: 'متوقف — بانتظار المسؤول' },
  'workflow.held.body': {
    en: 'Your hold reason has been sent to admin. They will reschedule or reassign this job.',
    ar: 'تم إرسال سبب الإيقاف إلى المسؤول. سيقوم بإعادة الجدولة أو إعادة التعيين.',
  },
  'workflow.hold.min': { en: 'Minimum 10 characters', ar: 'الحد الأدنى 10 أحرف' },
  'offline.banner': {
    en: 'Offline — changes will sync when you reconnect.',
    ar: 'غير متصل — ستتم المزامنة عند عودة الاتصال.',
  },
};

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: keyof typeof DICT | string) => string;
  dir: 'ltr' | 'rtl';
}

const Ctx = createContext<I18nCtx | undefined>(undefined);
const STORAGE_KEY = 'fin.app.locale';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    if (typeof window === 'undefined') return 'en';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'ar' || saved === 'en') return saved;
    return navigator.language?.startsWith('ar') ? 'ar' : 'en';
  });

  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    html.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      window.localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* private mode — best effort */
    }
  }, []);

  const value = useMemo<I18nCtx>(
    () => ({
      locale,
      setLocale,
      dir: locale === 'ar' ? 'rtl' : 'ltr',
      t: (key) => {
        const entry = DICT[key as keyof typeof DICT];
        if (!entry) return String(key);
        return entry[locale];
      },
    }),
    [locale, setLocale],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18nCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useI18n must be inside <I18nProvider>');
  return ctx;
}

/** Tiny EN/AR toggle button for headers. */
export function LocaleToggle() {
  const { locale, setLocale } = useI18n();
  const next: Locale = locale === 'en' ? 'ar' : 'en';
  return (
    <button
      onClick={() => setLocale(next)}
      className="rounded border border-white/30 px-2 py-1 text-xs hover:bg-white/10"
      aria-label={`Switch to ${next === 'ar' ? 'Arabic' : 'English'}`}
    >
      {locale === 'en' ? 'عربي' : 'EN'}
    </button>
  );
}
